/**
 * Declared operating-mode model (Phase-1 S1) — the TypeScript mirror of the
 * `EntityDef.data.operating_modes` JSON, a SIBLING of the physics `behavior`
 * graph on the same `entities.data` blob.
 *
 * SPEC IS LAW: these types mirror `platform-core/internal/twin/operating_modes.go`
 * (the `OperatingModes` / `ModeTransition` / `ModeTrigger` structs) exactly.
 * Property names use the WIRE keys (snake_case: `current_mode`, `on_event`,
 * `tag_hint`, `min_dwell_ms`, …) because the model rides `entities.data` verbatim
 * and is round-tripped through `POST /v1/twin/entities` as-is — there is no
 * serialization-mapping layer. The platform reads `data.entity.operating_modes`
 * to project `equipment_state` (basis `entity_view`); the portal editor (W4)
 * edits this shape in place.
 */

/**
 * The per-instance condition that fires a transition (the S4 predicate runtime
 * consumes it to mint the witnessed event; the event-only state writer never
 * evaluates it). Authoring ships the archetype default; a plant re-binds it to a
 * real gateway tag via `entity_tag_bindings.signal_config`.
 */
export interface ModeTrigger {
  /** Closed predicate vocabulary (e.g. analog_cross_above, coil_rising, count_pulse). */
  predicate?: string;
  /** Per-instance signal hint, rebased to the real bound tag per key. */
  tag_hint?: string;
  /** coil_equals target (register word). */
  value?: number | string | boolean;
  /** analog_cross level. */
  level?: number;
  /** analog_cross hysteresis (deadband). */
  deadband?: number;
  /** Crossing-direction hint. */
  edge?: "rising" | "falling" | string;
  /** Legacy alias for min_dwell_ms; the catalog ships min_dwell_ms. */
  dwell_ms?: number;
  /** Minimum dwell past the crossing before firing. */
  min_dwell_ms?: number;
  /** Debounce for coil edges + count pulses. */
  min_gap_ms?: number;
  /** elapsed_since anchor event. */
  after_event?: string;
  /** elapsed_since duration. */
  duration_ms?: number;
  /** analog | instrument | human | coil_register | derived. */
  source_class?: string;
}

/**
 * One declared edge of the FSM: a witnessed event whose kind == `on_event` moves
 * the equipment from any state in `from` to `to`. Where two transitions share an
 * `on_event` but diverge on `to` and `from` does not disambiguate, the platform
 * WITHHOLDS rather than guesses (PL-2).
 */
export interface ModeTransition {
  from: string[];
  to: string;
  on_event: string;
  trigger?: ModeTrigger;
}

/**
 * The predicates the platform's STREAMING runtime actually evaluates
 * (`platform-core/internal/twin/predicate_runtime.go`). The closed vocabulary
 * also names non-streaming members (coil_equals, count_delta, elapsed_since,
 * operator_push) that the runtime does NOT auto-fire yet; the editor offers only
 * these five so it never presents a predicate that would silently do nothing.
 */
export const STREAMING_PREDICATES = [
  "analog_cross_above",
  "analog_cross_below",
  "coil_rising",
  "coil_falling",
  "count_pulse",
] as const;

export type StreamingPredicate = (typeof STREAMING_PREDICATES)[number];

/** The per-equipment declared mode model, stored at `data.entity.operating_modes`. */
export interface OperatingModes {
  modes: string[];
  current_mode?: string;
  transitions: ModeTransition[];
  /**
   * Acts WITHIN a mode (e.g. a per-mould casting_produced count during pouring),
   * not transitions. The state resolver never reads them; kept verbatim for the
   * editor. Opaque here (the catalog ships objects {on_event, trigger_predicate,
   * why}).
   */
  non_mode_events?: unknown;
}
